import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ConfigProvider } from 'antd';
import { themes } from '../config/variables';
import { globalTokens, componentTokens } from '../config/themeVariable';
import { LOCAL_STORAGE_NAMES, ThemeMode } from '../../constants/constants';

type ThemeContextType = [keyof typeof themes, Dispatch<SetStateAction<keyof typeof themes>>];
const ThemeContext = createContext<ThemeContextType | null>(null);

export function ThemeProvider({ children }: Readonly<PropsWithChildren>) {
  const [themeColor, setThemeColor] = useState<keyof typeof themes>(() => {
    const storedTheme = localStorage.getItem(LOCAL_STORAGE_NAMES.theme)?.toLowerCase();

    if (
      storedTheme === ThemeMode.light.toLowerCase() ||
      storedTheme === ThemeMode.dark.toLowerCase() ||
      storedTheme === ThemeMode.system.toLowerCase()
    ) {
      return storedTheme as keyof typeof themes;
    }

    return ThemeMode.light.toLowerCase() as keyof typeof themes;
  });

  const value = useMemo<ThemeContextType>(() => [themeColor, setThemeColor], [themeColor]);

  useEffect(() => {
    const prefersDark = globalThis.matchMedia('(prefers-color-scheme: dark)');
    const applyTheme = (theme: string) => {
      document.documentElement.dataset.theme = theme;
    };

    const getOutlookTheme = (): 'light' | 'dark' | null => {
      try {
        const outlookTheme = Office?.context?.officeTheme;
        if (outlookTheme) {
          return outlookTheme.bodyBackgroundColor?.toLowerCase() === '#ffffff' ? 'light' : 'dark';
        }
      } catch (e) {
        console.info('Outlook theme detection failed:', e);
      }
      return null;
    };

    if (themeColor === ThemeMode.system.toLowerCase()) {
      // Try Outlook first
      const outlookTheme = getOutlookTheme();
      if (outlookTheme) {
        applyTheme(outlookTheme);
      } else {
        const resolvedTheme = prefersDark.matches ? ThemeMode.dark : ThemeMode.light;
        applyTheme(resolvedTheme.toLowerCase());
      }

      const handleSystemChange = (e: MediaQueryListEvent) => {
        const newTheme = e.matches ? ThemeMode.dark : ThemeMode.light;
        applyTheme(newTheme.toLowerCase());
      };

      prefersDark.addEventListener('change', handleSystemChange);

      // Listen for Outlook theme changes
      const onOfficeThemeChanged = () => {
        const updatedTheme = getOutlookTheme();
        if (updatedTheme) {
          applyTheme(updatedTheme);
        }
      };

      Office?.onReady?.(() => {
        if (Office.context.mailbox) {
          Office.context.mailbox.addHandlerAsync(Office.EventType.OfficeThemeChanged, () => {
            onOfficeThemeChanged();
          });
        }
      })?.catch((e: unknown) => {
        console.info('Failed to bind Office theme listener:', e);
      });

      return () => {
        prefersDark.removeEventListener('change', handleSystemChange);
      };
    }

    applyTheme(themeColor);

    localStorage.setItem(LOCAL_STORAGE_NAMES.theme, themeColor);
    return undefined;
  }, [themeColor]);

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          token: globalTokens,
          components: componentTokens,
          cssVar: true,
          hashed: false,
        }}
      >
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('Invalid hook call');
  }
  return context;
}
